import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  Building2,
  MapPin,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import {
  getPublicProfiles,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { resolveImage } from "../../../Utils/helper";
import { MARKETPLACE_FONTS } from "./MarketplaceConstants";
import marketplaceHomeBg from "../../../assets/images/marketplace/marketplace_home_bg.webp";
import equipmentImg from "../../../assets/images/marketplace/equipment.webp";
import teamsImg from "../../../assets/images/marketplace/teams.webp";
import team2Img from "../../../assets/images/marketplace/team2.webp";
import team3Img from "../../../assets/images/marketplace/team3.webp";
import team4Img from "../../../assets/images/marketplace/team4.webp";

const FontFace = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const Page = styled.main`
  min-height: 100vh;
  background: #101315;
  color: #e6e4dc;
  font-family: "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: #f7f8f5;
    color: #171717;
  }
`;

const Shell = styled.section`
  background: #101315;
  padding: 92px 0 118px;

  [data-marketplace-theme="light"] & {
    background: #f7f8f5;
  }
`;

const Container = styled.div`
  width: min(1080px, calc(100% - 72px));
  margin: 0 auto;

  @media (max-width: 720px) {
    width: min(100% - 32px, 1080px);
  }
`;

const Hero = styled.header`
  padding: 54px 0 58px;
  animation: ${fadeUp} 0.45s ease both;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 18px;
  color: #cbbfa7;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 26px;

  &::before {
    content: "";
    width: 42px;
    height: 1px;
    background: #cbbfa7;
  }

  [data-marketplace-theme="light"] & {
    color: #7a715f;

    &::before {
      background: #7a715f;
    }
  }
`;

const Title = styled.h1`
  max-width: 760px;
  margin: 0;
  color: #ebe9e2;
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: clamp(38px, 5vw, 64px);
  line-height: 1.1;
  font-weight: 800;

  span {
    color: #d0c4ad;
  }

  [data-marketplace-theme="light"] & {
    color: #171717;

    span {
      color: #6d6250;
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 62px;
  flex-wrap: wrap;
`;

const PillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const FilterPill = styled.button<{ $active?: boolean }>`
  border: 0;
  border-radius: 999px;
  min-height: 34px;
  padding: 0 20px;
  background: ${p => (p.$active ? "#ffe8b8" : "#1b1f21")};
  color: ${p => (p.$active ? "#201b12" : "#bdb6a7")};
  box-shadow: ${p => (p.$active ? "0 10px 22px rgba(255, 232, 184, 0.18)" : "none")};
  cursor: pointer;
  font: 800 12px/16px "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: ${p => (p.$active ? "#261f14" : "#e8e8e3")};
    color: ${p => (p.$active ? "#ffe8b8" : "#4f4a40")};
  }
`;

const RightTools = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ToolPill = styled.button`
  border: 0;
  min-height: 34px;
  border-radius: 999px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1b1f21;
  color: #cfc7b6;
  cursor: pointer;
  font: 800 12px/16px "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: #e8e8e3;
    color: #4f4a40;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.65fr);
  gap: 72px;
  align-items: start;
  margin-top: 10px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const LeftColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.78fr;
  gap: 72px;
  align-items: end;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 46px;
  padding-top: 0;
`;

const CardWrap = styled.article<{ $variant?: "wide" | "square" | "tall" | "small" }>`
  position: relative;
  border: 0;
  background: transparent;
  min-width: 0;

  ${p => p.$variant === "wide" && css`
    grid-column: 1 / -1;
  `}
`;

const CardButton = styled.button<{ $variant?: "wide" | "square" | "tall" | "small" }>`
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
`;

const ImageFrame = styled.div<{ $variant?: "wide" | "square" | "tall" | "small" }>`
  position: relative;
  overflow: hidden;
  border-radius: ${p => (p.$variant === "wide" ? "20px" : "22px")};
  height: ${p => {
    if (p.$variant === "wide") return "262px";
    if (p.$variant === "tall") return "360px";
    if (p.$variant === "small") return "280px";
    return "330px";
  }};
  background: #171b1d;
  box-shadow: 0 22px 60px rgba(0,0,0,0.32);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(16,19,21,0.05), rgba(16,19,21,0.15) 38%, rgba(16,19,21,0.72));
    pointer-events: none;
  }

  @media (max-width: 720px) {
    height: ${p => (p.$variant === "wide" ? "240px" : "310px")};
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: saturate(0.88) contrast(1.04);
  transition: transform 0.35s ease;

  ${CardButton}:hover & {
    transform: scale(1.035);
  }
`;

const Rating = styled.span`
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1;
  border-radius: 999px;
  padding: 5px 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #fff0c4;
  background: rgba(16, 19, 21, 0.72);
  backdrop-filter: blur(8px);
  font-size: 12px;
  font-weight: 800;
`;

const Tags = styled.div`
  position: absolute;
  left: 28px;
  top: 96px;
  z-index: 1;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 720px) {
    left: 20px;
    top: 72px;
  }
`;

const Tag = styled.span<{ $blue?: boolean }>`
  border-radius: 999px;
  padding: 5px 12px;
  color: ${p => (p.$blue ? "#8be7ff" : "#efe5cc")};
  background: rgba(16, 19, 21, 0.44);
  backdrop-filter: blur(8px);
  font-size: 11px;
  line-height: 14px;
  font-weight: 800;
`;

const OverlayInfo = styled.div`
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 26px;
  z-index: 1;
`;

const CardTitle = styled.h2<{ $large?: boolean }>`
  margin: 0 0 10px;
  color: #f3f2ed;
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: ${p => (p.$large ? "31px" : "20px")};
  line-height: 1.08;
  font-weight: 800;
`;

const CardLocation = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #d8d0bf;
  font-size: 14px;
  font-weight: 600;
`;

const ArrowBubble = styled.span`
  position: absolute;
  right: 36px;
  bottom: 38px;
  z-index: 2;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: #ffe8b8;
  color: #191510;
  display: grid;
  place-items: center;
`;

const TextBelow = styled.div`
  padding: 20px 4px 0;
`;

const Category = styled.div<{ $blue?: boolean }>`
  color: ${p => (p.$blue ? "#5bd7ff" : "#d0c4ad")};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const PlainTitle = styled.h3`
  margin: 0 0 7px;
  color: #e6e4dc;
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: 23px;
  line-height: 30px;
  font-weight: 800;

  [data-marketplace-theme="light"] & {
    color: #171717;
  }
`;

const PlainLocation = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #c2baa8;
  font-size: 14px;
  font-weight: 600;

  [data-marketplace-theme="light"] & {
    color: #605848;
  }
`;

const ExploreWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 94px;
`;

const ExploreButton = styled.button`
  border: 0;
  border-radius: 999px;
  min-width: 260px;
  min-height: 58px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: #1d2123;
  color: #d4ccbb;
  cursor: pointer;
  font: 800 13px/18px "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: #e8e8e3;
    color: #3e382e;
  }
`;

const SkeletonBase = css`
  background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.09), rgba(255,255,255,0.04));
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

const Skeleton = styled.div`
  height: 840px;
  border-radius: 22px;
  ${SkeletonBase}
`;

type DirectoryCard = {
  category: string;
  location: string;
  name: string;
  rating: string;
  image: string;
  id?: number;
  tags?: string[];
};

const FALLBACK_CARDS: DirectoryCard[] = [
  {
    name: "Lumina Creative House",
    category: "Commercial",
    location: "Los Angeles, CA",
    rating: "4.9 (120)",
    image: marketplaceHomeBg,
    tags: ["Commercial", "Feature"],
  },
  {
    name: "Nebula Post",
    category: "VFX & Post",
    location: "Vancouver, BC",
    rating: "5.0",
    image: equipmentImg,
  },
  {
    name: "Studio Blank",
    category: "Commercial",
    location: "New York, NY",
    rating: "4.7",
    image: team4Img,
  },
  {
    name: "Veritas Films",
    category: "Documentary",
    location: "London, UK",
    rating: "4.8",
    image: team2Img,
  },
  {
    name: "Oasis Productions",
    category: "Feature Film",
    location: "Atlanta, GA",
    rating: "4.9",
    image: teamsImg,
  },
];

const categoryFilters = ["All Agencies", "Commercial", "Feature Film", "Documentary", "VFX & Post"];

const pickCategory = (studio: MarketplaceProfile, index: number) => {
  const services = studio.services_offered || studio.work_categories || [];
  const matched = services.find(service => categoryFilters.slice(1).some(cat => service.toLowerCase().includes(cat.toLowerCase().split(" ")[0])));
  return matched || categoryFilters[(index % (categoryFilters.length - 1)) + 1];
};

const MarketplaceAgencyDirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [studios, setStudios] = useState<MarketplaceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All Agencies");

  const fetchStudios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicProfiles({ page: 1, page_size: 24, profile_type: "studio" });
      setStudios(res.results ?? []);
    } catch {
      setStudios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudios();
  }, [fetchStudios]);

  const cards = useMemo<DirectoryCard[]>(() => {
    if (!studios.length) return FALLBACK_CARDS;

    return studios.map((studio, index) => ({
      id: studio.id,
      name: studio.display_name,
      category: pickCategory(studio, index),
      location: [studio.city, studio.state || studio.country].filter(Boolean).join(", ") || "Global",
      rating: (studio.profile_avg_rating ?? 4.8).toFixed(1),
      image: (studio.profile_picture ? resolveImage(studio.profile_picture) : "") || FALLBACK_CARDS[index % FALLBACK_CARDS.length]?.image || marketplaceHomeBg,
      tags: (studio.services_offered || studio.work_categories || []).slice(0, 2),
    }));
  }, [studios]);

  const filteredCards = activeCategory === "All Agencies"
    ? cards
    : cards.filter(card => card.category.toLowerCase().includes(activeCategory.toLowerCase().split(" ")[0]));

  const visibleCards = (filteredCards.length ? filteredCards : cards).slice(0, 5);
  const [heroCard, secondCard, thirdCard, fourthCard, fifthCard] = visibleCards;

  const openCard = (card?: DirectoryCard) => {
    if (card?.id) {
      navigate(`/marketplace/agency/${card.id}`);
    }
  };

  const renderOverlayCard = (card: DirectoryCard | undefined) => (
    <CardWrap $variant="wide">
      <CardButton $variant="wide" onClick={() => openCard(card)}>
        <ImageFrame $variant="wide">
          <CardImage src={card?.image || marketplaceHomeBg} alt={card?.name || "Agency"} />
          <Rating><Star size={13} fill="#fff0c4" /> {card?.rating || "4.9"}</Rating>
          <Tags>
            {(card?.tags?.length ? card.tags : ["Commercial", "Feature"]).map(tag => <Tag key={tag}>{tag}</Tag>)}
          </Tags>
          <OverlayInfo>
            <CardTitle $large>{card?.name || "Lumina Creative House"}</CardTitle>
            <CardLocation><MapPin size={15} /> {card?.location || "Los Angeles, CA"}</CardLocation>
          </OverlayInfo>
          <ArrowBubble><ArrowRight size={21} /></ArrowBubble>
        </ImageFrame>
      </CardButton>
    </CardWrap>
  );

  const renderPlainCard = (card: DirectoryCard | undefined, variant: "square" | "tall" | "small", blue = false) => (
    <CardWrap $variant={variant}>
      <CardButton $variant={variant} onClick={() => openCard(card)}>
        <ImageFrame $variant={variant}>
          <CardImage src={card?.image || team3Img} alt={card?.name || "Agency"} />
          <Rating><Star size={13} fill="#fff0c4" /> {card?.rating || "4.8"}</Rating>
        </ImageFrame>
        <TextBelow>
          <Category $blue={blue}>{card?.category || "Documentary"}</Category>
          <PlainTitle>{card?.name || "Veritas Films"}</PlainTitle>
          <PlainLocation><Building2 size={14} /> {card?.location || "London, UK"}</PlainLocation>
        </TextBelow>
      </CardButton>
    </CardWrap>
  );

  return (
    <Page>
      <FontFace />
      <Shell>
        <Container>
          <Hero>
            <Eyebrow>Curated Roster</Eyebrow>
            <Title>
              The vanguard of visual storytelling. <span>Discover elite agencies pushing the boundaries of creative production.</span>
            </Title>
            <Toolbar>
              <PillGroup>
                {categoryFilters.map(filter => (
                  <FilterPill
                    key={filter}
                    $active={activeCategory === filter}
                    onClick={() => setActiveCategory(filter)}
                  >
                    {filter}
                  </FilterPill>
                ))}
              </PillGroup>
              <RightTools>
                <ToolPill><MapPin size={15} /> Global</ToolPill>
                <ToolPill aria-label="Filters"><SlidersHorizontal size={15} /></ToolPill>
              </RightTools>
            </Toolbar>
          </Hero>

          {loading ? (
            <Skeleton />
          ) : (
            <>
              <Grid>
                <LeftColumn>
                  {renderOverlayCard(heroCard)}
                  {renderPlainCard(secondCard, "square")}
                  {renderPlainCard(thirdCard, "small")}
                </LeftColumn>
                <RightColumn>
                  {renderPlainCard(fourthCard, "small")}
                  {renderPlainCard(fifthCard, "tall", true)}
                </RightColumn>
              </Grid>

              <ExploreWrap>
                <ExploreButton onClick={() => navigate("/marketplace/studios")}>
                  Explore Directory <ArrowDown size={18} />
                </ExploreButton>
              </ExploreWrap>
            </>
          )}
        </Container>
      </Shell>
    </Page>
  );
};

export default MarketplaceAgencyDirectoryPage;
